import { PointerEvent, ReactNode, useId, useRef, useState } from 'react'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface SocialReasonTooltipProps {
  /** The tooltip text; null renders the children alone. */
  reason: string | null
  /**
   * True when the wrapped control is disabled: the wrapper span joins the tab
   * order and points at a visually hidden copy of the reason, because a
   * disabled button gets no pointer events and no focus (E6).
   */
  isDisabled: boolean
  /**
   * True when the wrapped content is not a control (a label chip): the
   * wrapper joins the tab order and carries the reason for screen readers,
   * as for a disabled control, and a tap toggles the tooltip (touch screens
   * never hover).
   */
  isFocusable?: boolean
  /** On the tooltip content (`<testId>-tip`) and the wrapper (`<testId>-trigger`). */
  testId: string
  className?: string
  children: ReactNode
}

/**
 * Cost, cooldown or needs-credits tooltip on a social "Update now" control,
 * the `BillingLockedTooltip` wrapper-span pattern: the span is the trigger so
 * the tooltip still opens while the control is disabled. Also the market
 * reason on a post tile (`isFocusable`).
 */
export function SocialReasonTooltip(props: SocialReasonTooltipProps) {
  const { reason, isDisabled, isFocusable = false, testId, className, children } = props

  const [isTapOpen, setIsTapOpen] = useState(false)

  // A tap also focuses the wrapper just before its click: left to the tooltip, that focus opens it and the click closes it.
  const isTouchGesture = useRef(false)

  const reasonId = useId()

  const isOwnTabStop = isDisabled || isFocusable

  const onPointerDown = (event: PointerEvent<HTMLSpanElement>) => {
    isTouchGesture.current = event.pointerType === 'touch'
  }

  const onPointerCancel = () => {
    isTouchGesture.current = false
  }

  const onOpenChange = (isOpen: boolean) => {
    if (!isTouchGesture.current) {
      setIsTapOpen(isOpen)
    }
  }

  const onClick = () => {
    setIsTapOpen((current) => !current)
    // The tooltip's own close runs after this handler, in the same click.
    setTimeout(() => {
      isTouchGesture.current = false
    }, 0)
  }

  if (!reason) {
    return <>{children}</>
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip {...(isFocusable ? { open: isTapOpen, onOpenChange } : {})}>
        <TooltipTrigger asChild>
          <span
            onPointerDown={isFocusable ? onPointerDown : undefined}
            onPointerCancel={isFocusable ? onPointerCancel : undefined}
            onClick={isFocusable ? onClick : undefined}
            tabIndex={isOwnTabStop ? 0 : undefined}
            aria-describedby={isOwnTabStop ? reasonId : undefined}
            className={cn('inline-flex', className)}
            data-testid={`${testId}-trigger`}
          >
            {children}
            {isOwnTabStop ? (
              <span id={reasonId} className="sr-only" data-testid={`${testId}-reason`}>
                {reason}
              </span>
            ) : null}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px] text-xs" data-testid={`${testId}-tip`}>
          {reason}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
